/**
 * Hearts cell for restaurant tables: a red heart (filled when you hearted it) with the count as a
 * small badge on its top-right corner. Click to toggle. The summary rides along with the row and is
 * updated in the query cache by the toggle mutation, so the cell needs no state of its own.
 */
import { Heart } from "lucide-react";

import { cn } from "@/lib/utils";
import { useToggleHeart } from "../hooks/use-hearts";
import { Restaurant } from "../types/restaurants.types";

export function HeartCell({ restaurant }: { restaurant: Restaurant }) {
  const toggle = useToggleHeart(restaurant.id);
  const { count, heartedByMe: hearted } = restaurant.hearts ?? { count: 0, heartedByMe: false };

  return (
    <button
      type="button"
      aria-pressed={hearted}
      aria-label={hearted ? `Remove your heart (${count})` : `Heart this restaurant (${count})`}
      onClick={(e) => {
        e.stopPropagation();
        toggle.mutate(!hearted);
      }}
      className="relative inline-flex size-7 items-center justify-center rounded-sm text-brand transition-colors hover:bg-brand-soft"
    >
      <Heart className={cn("size-4", hearted && "fill-brand")} strokeWidth={1.75} aria-hidden />
      {count > 0 && (
        <span
          aria-hidden
          className="absolute -right-0.5 -top-0.5 inline-flex h-[13px] min-w-[13px] items-center justify-center rounded-full border border-brand bg-background px-[3px] text-[9px] font-semibold leading-none tabular-nums text-brand"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
